import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import PropTypes from "prop-types";
import * as commentActions from "~/store/actions/actions.comments.jsx";
import { openForm, closeForm } from "~/store/actions/actions.form.jsx";
import { fetchLastVisitTimestamp } from "~/store/actions/actions.lastVisit.jsx";
import { makeCommentIdsByParentSortedSelector } from "~/store/selectors/selector.comments.jsx";
import { getOpenForm } from "~/store/selectors/selector.forms.jsx";
import { getLoggedUser } from "~/store/selectors/selector.users.jsx";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import ConnectCommentList from "./component.comment_list.jsx";

const ObjectComments = ({ parentType, parentId, onAddComment, ...props }) => {
  const dispatch = useDispatch();
  const getCommentIdsByParentSortedSelector = useMemo(makeCommentIdsByParentSortedSelector, []);

  const items = useSelector((state) => getCommentIdsByParentSortedSelector(state, parentType, parentId));
  const editCommentId = useSelector((state) => getOpenForm(state, `${parentType}_${parentId}_comment`.toLowerCase()));
  const loggedUserId = useSelector((state) => getLoggedUser(state).get(`id`));

  const addComment = useCallback(
    (id, attributes) => {
      dispatch(commentActions.addComment(parentType, parentId, attributes, id)).then(() => {
        const slug = Angie.functions.slug(parentType);
        fetch({
          url: `${Angie.api_url}/access-logs/${slug}/${parentId}`,
          method: "PUT",
        });
        typeof onAddComment === "function" && onAddComment();
      });
    },
    [dispatch, onAddComment, parentId, parentType]
  );

  const editComment = useCallback(
    (id, attributes) => {
      dispatch(commentActions.editComment(id, attributes));
    },
    [dispatch]
  );
  const trashComment = useCallback(
    (id) => {
      dispatch(commentActions.moveToTrash(id, parentType, parentId));
    },
    [dispatch, parentId, parentType]
  );
  const fetchComments = useCallback(
    (page) => {
      dispatch(commentActions.fetchComments(parentId, parentType, page));
    },
    [dispatch, parentId, parentType]
  );
  const openFormHandler = useCallback(
    (id) => {
      dispatch(openForm(`${parentType}_${parentId}_comment`.toLowerCase(), id));
      dispatch(fetchLastVisitTimestamp(parentId, parentType, moment.utc().format(`X`)));
    },
    [dispatch, parentId, parentType]
  );
  const closeFormHandler = useCallback(
    (id) => {
      dispatch(closeForm(`${parentType}_${parentId}_comment`.toLowerCase(), id));
    },
    [dispatch, parentId, parentType]
  );

  return (
    <div className="object_comments">
      <ConnectCommentList
        closeForm={closeFormHandler}
        openForm={openFormHandler}
        fetchComments={fetchComments}
        trashComment={trashComment}
        editComment={editComment}
        addComment={addComment}
        loggedUserId={loggedUserId}
        editCommentId={editCommentId}
        items={items}
        parentType={parentType}
        parentId={parentId}
        {...props}
      />
    </div>
  );
};

ObjectComments.propTypes = {
  parentType: PropTypes.string,
  parentId: PropTypes.number,
  onAddComment: PropTypes.func,
};

export default ObjectComments;
